/**
 * https://ui.shadcn.com/docs/components/avatar
 */
"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";

import { cn } from "@/lib/utils";

const ThumbnailVariantContext = React.createContext<"square" | "circle">("circle");

const Thumbnail = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & {
    variant?: "square" | "circle";
  }
>(({ className, variant = "circle", ...props }, ref) => (
  <ThumbnailVariantContext.Provider value={variant}>
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex size-12 shrink-0 overflow-hidden",
        {
          "rounded-full": variant === "circle",
          "rounded-[6px]": variant === "square",
        },
        className,
      )}
      {...props}
    />
  </ThumbnailVariantContext.Provider>
));
Thumbnail.displayName = AvatarPrimitive.Root.displayName;

type ThumbnailImageProps = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>;

const ThumbnailImage = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Image>, ThumbnailImageProps>(
  ({ className, ...props }, ref) => (
    <AvatarPrimitive.Image ref={ref} className={cn("aspect-square h-full w-full", className)} {...props} />
  ),
);
ThumbnailImage.displayName = AvatarPrimitive.Image.displayName;

const ThumbnailFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(ThumbnailVariantContext);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center bg-borderLight text-xs font-semibold",
        {
          "rounded-full": variant === "circle",
          "rounded-[6px]": variant === "square",
        },
        className,
      )}
      {...props}
    />
  );
});
ThumbnailFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Thumbnail, ThumbnailFallback, ThumbnailImage };
